import {
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { Router } from '@angular/router';

import { Routes } from '@angular/router';

import { LanguageService } from '../../services/language.service';
import { ModuleRegistryService } from '../../services/module-registry.service';
import { MODULE_ROUTE_PREFIX, buildModuleRoutes } from './module-routes';

/**
 * Resolves the module registry and installs the module routes before the
 * application renders.
 *
 * Blocking is the right trade-off here: the dashboard is the first screen, and
 * it is made entirely of what the registry knows. Rendering first would mean
 * showing an empty dashboard and filling it in a moment later, which reads as
 * "no modules installed" for exactly as long as it takes to notice. It also has
 * to finish before the router's first navigation, or a bookmarked `/m/<id>`
 * would land on the fallback route.
 *
 * The cost is bounded — one small JSON file plus one entry-point chunk per
 * enabled module, fetched in parallel — and `load()` never rejects, so a broken
 * configuration cannot stall the bootstrap.
 *
 * Registration order matters: this must come before `provideRouter` in the
 * application config, since app initializers run in the order they are
 * provided and the router's initial navigation is one of them.
 */
export function provideModuleRegistry(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(async () => {
      const language = inject(LanguageService);
      const registry = inject(ModuleRegistryService);
      const router = inject(Router);

      // App initializers run concurrently, so the dependency is stated rather
      // than assumed: resolving a module merges its dictionary for the active
      // language, which has to be known by then. `initialize` is idempotent and
      // shared with the i18n initializer.
      await language.initialize();
      await registry.load();

      router.resetConfig(withModuleRoutes(router.config, buildModuleRoutes(registry)));
    }),
  ]);
}

/**
 * Inserts the module routes as children of the `modules` route.
 *
 * Ahead of its own children, so the `:moduleId` fallback only catches ids no
 * module claimed. Returns a fresh configuration rather than mutating the one
 * the router already holds.
 */
function withModuleRoutes(config: Routes, moduleRoutes: Routes): Routes {
  return config.map((route) =>
    route.path === MODULE_ROUTE_PREFIX
      ? { ...route, children: [...moduleRoutes, ...(route.children ?? [])] }
      : route,
  );
}
